import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { startCheckout } from '../../lib/metaPixel';
import { getPortalRevealed, setPortalRevealed, subscribePortalRevealed } from '../../lib/portalRevealState';
const EPIC_LOGO = 'https://cdn.shopify.com/s/files/1/0661/3847/2542/files/LOGO_AIN_FONDO_1.png?v=1779774021';
const GOLD = '#C9A84C';
const GOLD_LIGHT = '#E8D48B';
const PRICE = 89.88;
interface SecretPortalRevealProps {
  /** Analytics label. */
  signName?: string;
  /** Checkout URL for the $89.88 Sistema Completo. */
  checkoutUrl: string;
  /** Primary tone — defaults to deep gold. */
  primaryColor?: string;
  /** Accent tone — defaults to light gold. */
  accentColor?: string;
}
/**
 * SecretPortalReveal
 * ──────────────────
 * AAA cinematic interactive portal that lives in place of the legacy
 * "Acceso Total" card. Two visual states inside the same zone:
 *
 *   1. SEALED  — dark, atmospheric. Epic LOS 12 logo floats in the center
 *                surrounded by a slow gold halo, drifting particles, faint
 *                runes and a twelve-fold sacred geometry mandala. Minimal
 *                cryptic copy. The whole logo region is the trigger.
 *
 *   2. OPENED  — the same zone transforms in place: the halo bursts, the
 *                particles accelerate, runes flare, and the $89.88 LOS 12
 *                REINOS reveal cascades in. No popup, no scroll, no route
 *                change. A small "Sellar de nuevo" lets the user collapse.
 */
export function SecretPortalReveal({
  signName = 'Sistema Completo',
  checkoutUrl,
  primaryColor = GOLD,
  accentColor = GOLD_LIGHT
}: SecretPortalRevealProps) {
  // Always-open reveal — the offer is shown unlocked on every page.
  const open = true;
  // Deterministic positions so the particles / runes don't reflow each render.
  const particles = useMemo(() => Array.from({
    length: 22
  }).map((_, i) => ({
    x: (i * 41 + 13) % 100,
    y: (i * 23 + 7) % 100,
    size: 1 + i * 0.7 % 2.4,
    delay: i * 0.17 % 3.2,
    dur: 5 + i * 0.5 % 5
  })), []);
  const runes = useMemo(() => [{
    glyph: '♈',
    x: 12,
    y: 18
  }, {
    glyph: '♉',
    x: 86,
    y: 22
  }, {
    glyph: '♊',
    x: 8,
    y: 48
  }, {
    glyph: '♋',
    x: 92,
    y: 52
  }, {
    glyph: '♌',
    x: 16,
    y: 78
  }, {
    glyph: '♍',
    x: 84,
    y: 82
  }, {
    glyph: '♎',
    x: 50,
    y: 8
  }, {
    glyph: '♏',
    x: 50,
    y: 92
  }, {
    glyph: '♐',
    x: 24,
    y: 35
  }, {
    glyph: '♑',
    x: 76,
    y: 38
  }, {
    glyph: '♒',
    x: 28,
    y: 64
  }, {
    glyph: '♓',
    x: 72,
    y: 66
  }], []);
  const handleEnter = () => {
    startCheckout(signName, checkoutUrl, PRICE);
  };
  return <motion.div layout transition={{
    layout: {
      duration: 1.1,
      ease: [0.16, 1, 0.3, 1]
    }
  }} className="relative w-full max-w-4xl mx-auto rounded-3xl overflow-hidden" style={{
    background: 'radial-gradient(ellipse at 50% 35%, rgba(20,16,8,0.95) 0%, rgba(6,5,9,0.98) 60%, #030305 100%)',
    border: `1px solid ${accentColor}26`,
    boxShadow: `inset 0 0 60px rgba(0,0,0,0.85), 0 30px 80px rgba(0,0,0,0.7), 0 0 60px ${primaryColor}15`
  }} data-id="element-741">
      {/* ─── Atmospheric layers — always present ─────────────────────── */}

      {/* Deep vignette */}
      <div className="absolute inset-0 pointer-events-none" style={{
      background: 'radial-gradient(ellipse at center, transparent 30%, rgba(0,0,0,0.7) 100%)'
    }} data-id="element-742" />

      {/* Slow drifting smoke / haze */}
      <motion.div className="absolute inset-0 pointer-events-none" animate={{
      opacity: open ? [0.35, 0.6, 0.35] : [0.15, 0.3, 0.15]
    }} transition={{
      duration: 7,
      repeat: Infinity,
      ease: 'easeInOut'
    }} style={{
      background: `radial-gradient(ellipse at 50% 50%, ${primaryColor}1F 0%, transparent 65%)`,
      filter: 'blur(50px)'
    }} data-id="element-743" />

      {/* Twelve-fold sacred geometry — counter-rotating layers */}
      <motion.div className="absolute inset-0 pointer-events-none flex items-center justify-center" animate={{
      opacity: open ? 0.22 : 0.1
    }} transition={{
      duration: 1.4,
      ease: [0.16, 1, 0.3, 1]
    }} data-id="element-744">
        <motion.svg viewBox="0 0 600 600" className="w-[140%] h-[140%] max-w-none" animate={{
        rotate: 360
      }} transition={{
        duration: 220,
        repeat: Infinity,
        ease: 'linear'
      }} data-id="element-745">
          <circle cx="300" cy="300" r="280" stroke={accentColor} strokeWidth="0.5" fill="none" opacity="0.7" data-id="element-746" />
          <circle cx="300" cy="300" r="200" stroke={accentColor} strokeWidth="0.4" strokeDasharray="3 5" fill="none" opacity="0.6" data-id="element-747" />
          {Array.from({
          length: 12
        }).map((_, i) => <line key={i} x1="300" y1="30" x2="300" y2="570" stroke={accentColor} strokeWidth="0.4" opacity="0.5" transform={`rotate(${i * 30} 300 300)`} data-id="element-748" />)}
        </motion.svg>
      </motion.div>

      <motion.div className="absolute inset-0 pointer-events-none flex items-center justify-center" animate={{
      opacity: open ? 0.28 : 0.12
    }} transition={{
      duration: 1.4
    }} data-id="element-749">
        <motion.svg viewBox="0 0 400 400" className="w-[80%] h-[80%] max-w-none" animate={{
        rotate: -360
      }} transition={{
        duration: 160,
        repeat: Infinity,
        ease: 'linear'
      }} data-id="element-750">
          <polygon points="200,40 360,300 40,300" stroke={primaryColor} strokeWidth="0.5" fill="none" opacity="0.5" data-id="element-751" />
          <polygon points="200,360 40,100 360,100" stroke={primaryColor} strokeWidth="0.5" fill="none" opacity="0.5" data-id="element-752" />
        </motion.svg>
      </motion.div>

      {/* Floating runes (12 zodiac glyphs) */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden" data-id="element-753">
        {runes.map((r, i) => <motion.div key={r.glyph} className="absolute font-serif select-none" style={{
        left: `${r.x}%`,
        top: `${r.y}%`,
        transform: 'translate(-50%, -50%)',
        color: accentColor,
        fontSize: open ? '18px' : '14px',
        textShadow: open ? `0 0 14px ${primaryColor}, 0 0 28px ${accentColor}` : `0 0 8px ${primaryColor}80`
      }} animate={{
        opacity: open ? [0.55, 0.95, 0.55] : [0.1, 0.28, 0.1],
        y: [0, -6, 0]
      }} transition={{
        duration: open ? 3.5 : 6 + i % 4,
        repeat: Infinity,
        ease: 'easeInOut',
        delay: i * 0.25 % 2.5
      }} data-id="element-754">
            {r.glyph}
          </motion.div>)}
      </div>

      {/* Floating gold particles */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden" data-id="element-755">
        {particles.map((p, i) => <motion.div key={i} className="absolute rounded-full" style={{
        left: `${p.x}%`,
        top: `${p.y}%`,
        width: p.size,
        height: p.size,
        backgroundColor: accentColor,
        boxShadow: `0 0 8px ${accentColor}`
      }} animate={{
        opacity: open ? [0, 0.95, 0] : [0, 0.55, 0],
        y: open ? [-4, -90] : [-4, -45],
        x: [0, (i % 5 - 2) * 6]
      }} transition={{
        duration: open ? p.dur * 0.55 : p.dur,
        repeat: Infinity,
        ease: 'easeInOut',
        delay: p.delay
      }} data-id="element-756" />)}
      </div>

      {/* ─── Central halo — intensifies on open ──────────────────────── */}
      <motion.div className="absolute left-1/2 top-[42%] sm:top-[40%] -translate-x-1/2 -translate-y-1/2 pointer-events-none" animate={{
      opacity: open ? [0.7, 1, 0.7] : [0.35, 0.55, 0.35],
      scale: open ? [1, 1.15, 1] : [0.95, 1.05, 0.95]
    }} transition={{
      duration: 4.2,
      repeat: Infinity,
      ease: 'easeInOut'
    }} style={{
      width: 360,
      height: 360,
      background: `radial-gradient(circle at center, ${primaryColor}77 0%, ${accentColor}22 40%, transparent 70%)`,
      filter: 'blur(36px)',
      maxWidth: '95vw'
    }} data-id="element-757" />

      {/* ─── Burst ring on transition ────────────────────────────────── */}
      <AnimatePresence data-id="element-758">
        {open && <motion.div key="burst" className="absolute left-1/2 top-[42%] sm:top-[40%] -translate-x-1/2 -translate-y-1/2 rounded-full pointer-events-none" initial={{
        scale: 0.2,
        opacity: 0.9
      }} animate={{
        scale: 3,
        opacity: 0
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 1.4,
        ease: [0.16, 1, 0.3, 1]
      }} style={{
        width: 200,
        height: 200,
        border: `1px solid ${accentColor}`,
        boxShadow: `0 0 40px ${primaryColor}, inset 0 0 30px ${accentColor}`
      }} data-id="element-759" />}
      </AnimatePresence>

      {/* ─── Foreground content (sealed / opened) ─────────────────────── */}
      <div className="relative" data-id="element-760">
        <AnimatePresence mode="wait" initial={false} data-id="element-761">
          {false /* ════════════════ SEALED (permanently unlocked) ════════════════ */ ? <motion.button key="sealed" type="button" aria-label="Desbloquear acceso oculto" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0,
          filter: 'blur(14px)',
          scale: 0.96
        }} transition={{
          duration: 0.85,
          ease: [0.16, 1, 0.3, 1]
        }} className="group relative w-full flex flex-col items-center justify-center px-5 py-16 sm:px-8 sm:py-20 md:py-24 cursor-pointer" whileHover={{}} data-id="element-762">
              {/* Epic floating logo */}
              <motion.div className="relative flex items-center justify-center mb-10 sm:mb-12" animate={{
            y: [0, -10, 0]
          }} transition={{
            duration: 6,
            repeat: Infinity,
            ease: 'easeInOut'
          }} data-id="element-763">
                {/* Logo halo — gentle breathing */}
                <motion.div className="absolute inset-0 rounded-full pointer-events-none" animate={{
              opacity: [0.5, 0.85, 0.5],
              scale: [0.95, 1.08, 0.95]
            }} transition={{
              duration: 4.5,
              repeat: Infinity,
              ease: 'easeInOut'
            }} style={{
              background: `radial-gradient(circle at center, ${primaryColor}66 0%, ${accentColor}22 45%, transparent 70%)`,
              filter: 'blur(28px)',
              transform: 'scale(1.4)'
            }} data-id="element-764" />

                <motion.img src={EPIC_LOGO} alt="Sigilo LOS 12 — acceso oculto" className="relative w-[180px] sm:w-[220px] md:w-[260px] h-auto select-none" draggable={false} animate={{
              scale: [1, 1.025, 1],
              rotate: [0, 0.4, -0.4, 0]
            }} transition={{
              duration: 7,
              repeat: Infinity,
              ease: 'easeInOut'
            }} style={{
              filter: `drop-shadow(0 0 18px ${primaryColor}88) drop-shadow(0 0 38px ${accentColor}55)`
            }} whileHover={{
              scale: 1.04,
              filter: `drop-shadow(0 0 30px ${primaryColor}) drop-shadow(0 0 60px ${accentColor}AA)`
            }} data-id="element-765" />
              </motion.div>

              {/* Cryptic copy */}
              <motion.p className="sm:text-[11.5px] tracking-[0.45em] uppercase font-serif mb-4 text-center font-[700] text-[#ECC020] text-[14px]" style={{
            textShadow: `0 0 14px ${primaryColor}80`
          }} animate={{
            opacity: [0.6, 1, 0.6]
          }} transition={{
            duration: 4,
            repeat: Infinity,
            ease: 'easeInOut'
          }} data-id="element-766">
                - Acceso Oculto -
              </motion.p>

              <p className="text-[20px] sm:text-[24px] md:text-[26px] font-serif italic text-center leading-snug mb-3 max-w-md px-2 pl-[7px] pr-[7px]" style={{
            color: '#F5F0E1',
            textShadow: `0 0 22px ${primaryColor}55, 0 2px 14px rgba(0,0,0,0.8)`
          }} data-id="element-767">
                Algunos ven más allá de su propio Reino.
              </p>

              <p className="text-[14.5px] sm:text-[20px] font-serif italic text-center max-w-xs px-4 leading-snug text-[#EFB30C]" data-id="element-768">
                - Ve más allá -
              </p>

              {/* Subtle pulse hint */}
              <motion.div className="mt-8 flex items-center gap-2" animate={{
            opacity: [0.4, 0.85, 0.4]
          }} transition={{
            duration: 2.4,
            repeat: Infinity,
            ease: 'easeInOut'
          }} data-id="element-769">
                <div className="w-1.5 h-1.5 rounded-full" style={{
              backgroundColor: primaryColor,
              boxShadow: `0 0 8px ${primaryColor}`
            }} data-id="element-770" />
                <span className="sm:text-[18px] tracking-[0.3em] sm:tracking-[0.4em] uppercase font-serif font-semibold text-[20px] text-[#F9D507] bg-[#000000]" data-id="element-771">
                  Toca aquí para desbloquearlo
                </span>
                <div className="w-1.5 h-1.5 rounded-full" style={{
              backgroundColor: primaryColor,
              boxShadow: `0 0 8px ${primaryColor}`
            }} data-id="element-772" />
              </motion.div>
            </motion.button> /* ════════════════ OPENED ════════════════ */ : <motion.div key="opened" initial={{
          opacity: 0,
          filter: 'blur(18px)',
          scale: 0.96
        }} animate={{
          opacity: 1,
          filter: 'blur(0px)',
          scale: 1
        }} exit={{
          opacity: 0,
          filter: 'blur(10px)'
        }} transition={{
          duration: 1.1,
          ease: [0.16, 1, 0.3, 1],
          delay: 0.2
        }} className="relative px-5 py-14 sm:px-10 sm:py-16 md:py-20 text-center" data-id="element-773">
              {/* Active logo — smaller, brighter */}
              <motion.div className="relative flex items-center justify-center mb-8" initial={{
            scale: 1.1,
            y: 10
          }} animate={{
            scale: 1,
            y: 0
          }} transition={{
            delay: 0.35,
            duration: 0.9
          }} data-id="element-774">
                <motion.div className="absolute inset-0 rounded-full pointer-events-none" animate={{
              opacity: [0.7, 1, 0.7],
              scale: [1, 1.12, 1]
            }} transition={{
              duration: 4,
              repeat: Infinity,
              ease: 'easeInOut'
            }} style={{
              background: `radial-gradient(circle at center, ${primaryColor}99 0%, ${accentColor}33 40%, transparent 70%)`,
              filter: 'blur(34px)',
              transform: 'scale(1.6)'
            }} data-id="element-775" />
                <img src={EPIC_LOGO} alt="Sigilo LOS 12 activado" className="relative w-[130px] sm:w-[160px] h-auto select-none" draggable={false} style={{
              filter: `drop-shadow(0 0 28px ${primaryColor}) drop-shadow(0 0 50px ${accentColor}99)`
            }} data-id="element-776" />
              </motion.div>

              {/* Eyebrow */}
              <motion.div className="flex items-center justify-center gap-3 mb-4" initial={{
            opacity: 0,
            y: -6
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.55,
            duration: 0.7
          }} data-id="element-777">
                <div className="h-px w-8 sm:w-12" style={{
              background: `linear-gradient(90deg, transparent, ${accentColor})`
            }} data-id="element-778" />
                <span className="text-[11px] sm:text-[12px] tracking-[0.4em] sm:tracking-[0.5em] uppercase font-serif font-semibold" style={{
              color: accentColor,
              textShadow: `0 0 14px ${primaryColor}`
            }} data-id="element-779">
                  Acceso Total al Sistema
                </span>
                <div className="h-px w-8 sm:w-12" style={{
              background: `linear-gradient(90deg, ${accentColor}, transparent)`
            }} data-id="element-780" />
              </motion.div>

              {/* Title */}
              <motion.h3 className="text-[34px] sm:text-[44px] md:text-[54px] font-serif font-bold leading-[1.05] mb-5 tracking-tight" style={{
            color: '#FAF5E8',
            textShadow: `0 0 36px ${primaryColor}77, 0 4px 22px rgba(0,0,0,0.7)`
          }} initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.7,
            duration: 1,
            ease: [0.16, 1, 0.3, 1]
          }} data-id="element-781">
                Los 12 Reinos
              </motion.h3>

              {/* Lead */}
              <motion.p className="text-[18px] sm:text-[20px] md:text-[22px] font-serif font-semibold leading-snug mb-3 max-w-2xl mx-auto px-2" style={{
            color: '#FFFFFF'
          }} initial={{
            opacity: 0,
            y: 6
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.9,
            duration: 0.8
          }} data-id="element-782">
                No somos un solo Reino.
              </motion.p>

              <motion.p className="sm:text-[16px] md:text-[17px] italic mb-10 max-w-xl mx-auto px-3 text-[16px] font-['DM_Sans',_sans-serif]" style={{
            color: `${accentColor}DD`
          }} initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            delay: 1.1,
            duration: 0.8
          }} data-id="element-783">
                Dentro de nosotros existen múltiples patrones, emociones y
                versiones internas. Accede al sistema completo de identidad
                emocional y explora las 12 fuerzas que moldean la experiencia
                humana.
              </motion.p>

              {/* Twelve glyph flow — subtle horizontal river */}
              <motion.div className="flex items-center justify-center gap-3 sm:gap-4 mb-10 flex-wrap max-w-md mx-auto" initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            delay: 1.3,
            duration: 0.9
          }} data-id="element-784">
                {['♈', '♉', '♊', '♋', '♌', '♍', '♎', '♏', '♐', '♑', '♒', '♓'].map((g, i) => <motion.span key={g} className="font-serif select-none" style={{
              color: accentColor,
              fontSize: '16px',
              textShadow: `0 0 10px ${primaryColor}99`
            }} initial={{
              opacity: 0,
              y: 4
            }} animate={{
              opacity: [0.55, 0.95, 0.55],
              y: 0
            }} transition={{
              opacity: {
                duration: 3 + i % 4,
                repeat: Infinity,
                ease: 'easeInOut',
                delay: i * 0.12
              },
              y: {
                delay: 1.35 + i * 0.05,
                duration: 0.6
              }
            }} data-id="element-785">
                    {g}
                  </motion.span>)}
              </motion.div>

              {/* Hairline */}
              <motion.div className="mx-auto mb-6 h-px" initial={{
            opacity: 0,
            scaleX: 0
          }} animate={{
            opacity: 1,
            scaleX: 1
          }} transition={{
            delay: 1.5,
            duration: 0.8
          }} style={{
            width: '55%',
            maxWidth: 280,
            background: `linear-gradient(90deg, transparent, ${accentColor}60, transparent)`,
            transformOrigin: 'center'
          }} data-id="element-786" />

              {/* Price */}
              <motion.div className="mb-8" initial={{
            opacity: 0,
            y: 8
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 1.55,
            duration: 0.8
          }} data-id="element-787">
                <div className="relative inline-block" data-id="element-788">
                  <motion.div className="absolute inset-0 -m-6 pointer-events-none" animate={{
                opacity: [0.4, 0.8, 0.4]
              }} transition={{
                duration: 4,
                repeat: Infinity,
                ease: 'easeInOut'
              }} style={{
                background: `radial-gradient(ellipse at center, ${primaryColor}88 0%, transparent 70%)`,
                filter: 'blur(22px)'
              }} data-id="element-789" />
                  <span className="relative text-[56px] sm:text-[68px] md:text-[76px] font-serif font-bold leading-none tracking-tight" style={{
                color: '#F5E9B8',
                textShadow: `0 0 30px ${primaryColor}, 0 0 60px ${accentColor}66`
              }} data-id="element-790">
                    ${PRICE}
                  </span>
                </div>
              </motion.div>

              {/* Premium CTA */}
              <motion.button onClick={handleEnter} className="relative w-full max-w-md mx-auto rounded-2xl px-5 py-5 sm:py-6 font-bold text-[13px] sm:text-[15px] tracking-[0.18em] overflow-hidden" style={{
            background: `linear-gradient(135deg, ${primaryColor} 0%, ${accentColor} 50%, ${primaryColor} 100%)`,
            backgroundSize: '200% 100%',
            color: '#070710',
            boxShadow: `0 18px 44px rgba(0,0,0,0.6), 0 0 40px ${primaryColor}AA, 0 0 80px ${accentColor}55, inset 0 1px 0 rgba(255,255,255,0.3)`
          }} initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0,
            backgroundPosition: ['0% 50%', '100% 50%', '0% 50%']
          }} transition={{
            opacity: {
              delay: 1.75,
              duration: 0.7
            },
            y: {
              delay: 1.75,
              duration: 0.7
            },
            backgroundPosition: {
              duration: 9,
              repeat: Infinity,
              ease: 'easeInOut'
            }
          }} whileHover={{
            scale: 1.02,
            boxShadow: `0 22px 56px rgba(0,0,0,0.65), 0 0 56px ${primaryColor}, 0 0 100px ${accentColor}88, inset 0 1px 0 rgba(255,255,255,0.35)`
          }} whileTap={{
            scale: 0.985
          }} data-id="element-791">
                {/* Sheen sweep */}
                <motion.span className="absolute inset-0 pointer-events-none" animate={{
              x: ['-120%', '120%']
            }} transition={{
              duration: 5,
              repeat: Infinity,
              ease: 'easeInOut',
              repeatDelay: 1.6
            }} style={{
              background: 'linear-gradient(115deg, transparent 30%, rgba(255,255,255,0.4) 50%, transparent 70%)'
            }} data-id="element-792" />
                <span className="relative" data-id="element-793">ACCEDER AL SISTEMA COMPLETO</span>
              </motion.button>

              {/* Final cinematic note */}
              <motion.p className="mt-8 mb-3 font-serif font-semibold tracking-[0.2em] uppercase text-[12.5px] sm:text-[13.5px]" style={{
            color: accentColor,
            textShadow: `0 0 16px ${primaryColor}90`
          }} initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            delay: 1.95,
            duration: 0.7
          }} data-id="element-794">
                Vive una Experiencia Inmersiva
              </motion.p>

              <motion.p className="max-w-lg mx-auto text-[13.5px] sm:text-[14.5px] italic leading-relaxed px-4 font-['DM_Sans',_sans-serif]" style={{
            color: `${accentColor}B8`
          }} initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            delay: 2.1,
            duration: 0.7
          }} data-id="element-795">
                Después de tu compra recibirás un acceso exclusivo a tu Portal
                del Reino. Todos tus archivos y experiencias estarán disponibles
                dentro de tu espacio privado, diseñado para explorar tu universo
                de forma inmersiva.
              </motion.p>
            </motion.div>}
        </AnimatePresence>
      </div>
    </motion.div>;
}